import type { ResearchInformationDetailMap } from './research-information';

/**
 * 危化品事故六类典型问题、30 项信息需求的逐项详解。
 *
 * 文字只解释为什么需要、拿到什么、缺失会怎样、能说明与不能说明什么；
 * 不给出中和、堵漏、转移、灭火介质、警戒半径、疏散距离或任何可被当作行动阈值的数值。
 */
export const chemicalInformationDetails: ResearchInformationDetailMap = {
  /* ---------------------------------------------------- 化学品身份与危险特性不明 */
  'chemical-identity|物质名称与识别编号': {
    why: '名称与编号是把现场发现对应到一条可查记录的唯一入口。没有它，危险特性、相容性与防护资料都无从检索。',
    what: ['联合国编号或 CAS 号（如来源提供）', '中英文名称与别名', '记录来源与记录时间'],
    risk: '只有俗称或外观描述时，不同物质容易被混为一谈，后续资料查到的可能不是同一种东西。',
    boundary: '能说明现场标识声称的是什么物质；不能据此确认容器内实际装载物。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation: '条例要求危险化学品登记与信息提供，可用于说明企业为何应当保留名称与编号。',
      },
    ],
  },
  'chemical-identity|危险标签与运输单证': {
    why: '标签与运输单证反映的是申报信息，必须与实际装载物分开看待，不能当作对现场内容的确认。',
    what: ['危险货物标签内容', '运输单证编号与申报单位', '标签是否完整、是否与单证一致'],
    risk: '未核对申报与实际，混装、错报或残留物就无法暴露，事故初期容易按错误的物质准备。',
    boundary: '能说明申报了什么；不能证明容器里就是申报的那种物质。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation: '条例对危险化学品经营、运输与信息提供的要求，是标签与单证制度的依据。',
      },
    ],
  },
  'chemical-identity|SDS 版本与内容': {
    why: '安全技术说明书（SDS）是了解危险特性、相容性与防护提示的主要书面来源，但必须先确认版本与供应企业。',
    what: ['SDS 版本号与编制日期', '提供该版本的企业', '与标签、单证记载是否一致'],
    risk: '使用过期版本会给出与实际产品不符的危险特性，也会漏掉配方调整带来的变化。',
    boundary: '能说明生产或供应企业申报的特性；标准网页本身不是具体化学品的 SDS。',
    sources: [
      {
        id: 'SRC-GBT-16483',
        relation: '国家标准规定 SDS 的内容与项目顺序，可用于判断一份说明书是否完整。',
      },
      {
        id: 'SRC-REG-591',
        relation: '条例要求向用户提供安全技术说明书，可用于说明这份文件从何而来。',
      },
    ],
  },
  'chemical-identity|混合物、浓度、物理状态与数量': {
    why: '危害程度与可能的释放行为取决于浓度、物理状态和数量，而不只取决于物质名称。',
    what: ['混合物组成与大致配比', '浓度范围', '物理状态与现场记载的数量'],
    risk: '只写名称不写浓度与数量，会低估释放后可能影响的范围与持续时间。',
    boundary: '能说明台账与资料记载的存量与状态；不代表事故时刻的实际存量。',
    sources: [
      {
        id: 'SRC-GB-18218',
        relation: '重大危险源辨识标准说明物质与数量为何要成组记录，可用于理解存量信息的作用。',
      },
    ],
  },
  'chemical-identity|混装、残留物与未申报物质线索': {
    why: '身份类信息中最大的缺口往往不是“查不到”，而是“没有人申报”。这类线索必须单独列为待核实项。',
    what: ['企业技术人员的说明', '历史台账与运输记录', '容器内残留物或异味等现场线索（注明来源）'],
    risk: '忽略未申报物，会让后续监测与研判建立在一份不完整的物质清单上。',
    boundary: '能说明还存在哪些未知；不能据此推断场内一定存在某种物质。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation: '登记与信息提供制度，可用于说明未申报物质为何是制度层面的风险点。',
      },
      {
        id: 'SRC-GB-18218',
        relation: '辨识制度要求掌握全部危险化学品种类与数量，可用于说明清单完整性为何重要。',
      },
    ],
  },

  /* ---------------------------------------------------- 泄漏源与释放状态不清 */
  'release-source|释放位置与载体': {
    why: '先要明确泄漏来自哪台设备、哪段管线还是哪辆运输车。载体的性质决定后续资料从哪里调取。',
    what: ['释放位置描述与可核对的编号', '设备、容器或管线信息', '所属工艺单元或车辆信息'],
    risk: '位置含糊时，监测布点与资料调取都会失去方向，也无法判断相邻危险源。',
    boundary: '能说明报告指向的位置与载体；不能证明源头已经确认。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation: '条例对危险源控制与危害区域测定的要求，是定位与记录释放源头的制度依据。',
      },
      {
        id: 'SRC-AQ-3052',
        relation: '应急救援指挥导则的基本程序，可用于说明现场信息应按什么顺序组织。',
      },
    ],
  },
  'release-source|首次发现时间与报告来源': {
    why: '时间线需要一个起点，而起点必须写明是谁、通过什么方式发现的。',
    what: ['发现时间', '报警或报告渠道', '报告人身份与所属单位'],
    risk: '起点不实或来源不明，整条时间线都会偏移，与其他部门记录对不上。',
    boundary: '能说明报告抵达的时间；不代表释放从那一刻才开始。',
    sources: [
      { id: 'SRC-AQ-3052', relation: '指挥导则对信息报告与程序的规定，可用于组织时间线起点。' },
      {
        id: 'SRC-REG-708',
        relation: '应急条例对事故报告与应急响应的规定，可用于说明报告环节的制度依据。',
      },
    ],
  },
  'release-source|释放现象的持续变化': {
    why: '判断释放是否仍在持续，只能依靠连续观察，而不是某一次描述。',
    what: [
      '各时段的观察记录',
      '可见现象的变化（颜色、声音、结霜、地面痕迹等）',
      '观察位置与观察方式',
    ],
    risk: '用一次描述代替变化过程，会把仍在持续的释放当成已经控制。',
    boundary: '能说明现象如何变化；不能据此推算释放量或影响范围。',
    sources: [
      {
        id: 'SRC-AQ-3052',
        relation: '指挥程序要求持续掌握现场情况，可用于说明连续观察记录的作用。',
      },
      {
        id: 'SRC-DRAFT-2026',
        relation: '征求意见稿拟明确应急救援基本程序与现场处置要点；仅为征求意见稿，不是现行规范。',
      },
    ],
  },
  'release-source|固定与移动监测记录': {
    why: '监测记录要带点位、时间和设备状态，才能与现场观察互相印证。',
    what: ['点位与采样时间', '设备类型、量程与校准状态', '读数以及被标记为异常的数据点'],
    risk: '没有点位与时间的读数，无法与具体位置对应，也无法判断两次读数是否可比。',
    boundary: '能说明某些点位在某个时刻的读数；不能代表整个区域，也不能当作通用阈值。',
    sources: [
      {
        id: 'SRC-AQ-3052',
        relation: '指挥导则对现场信息掌握的要求，可用于说明监测记录应如何组织。',
      },
      {
        id: 'SRC-GB-18218',
        relation: '辨识标准说明物质与数量记录的作用，可用于理解台账与监测为何要并行。',
      },
    ],
  },
  'release-source|气象记录': {
    why: '气象条件影响释放物的移动与稀释，需要用带地点和时间的记录，而不是笼统的天气描述。',
    what: ['风向与风速', '温度与大气稳定度描述', '记录来源、地点与时间'],
    risk: '用一句“天气不好”代替记录，无法解释不同监测点位之间为什么会出现差异。',
    boundary: '能说明环境条件；不能据此计算扩散范围或警戒距离。',
    sources: [
      {
        id: 'SRC-DRAFT-2026',
        relation: '征求意见稿拟对现场气象等条件提出要求；仅为征求意见稿，仅供研究参考。',
      },
      {
        id: 'SRC-AQ-3052',
        relation: '指挥导则的现场信息组织要求，可用于说明气象记录应与其他观察并列。',
      },
    ],
  },

  /* ---------------------------------------------------- 人员暴露与安全状态不明 */
  'human-exposure|人员数量与位置': {
    why: '需要知道可能受影响的有哪些人、他们在哪里。数量与位置是所有人员信息的基础。',
    what: ['企业考勤、访客与承包商登记', '位置描述及其核实状态', '人员所属单位或队伍'],
    risk: '漏掉未登记人员，会让医疗与人员安全工作建立在缩小的范围上。',
    boundary: '能说明登记与报告记载的人数与位置；不能据此判断人员安全。',
    sources: [
      {
        id: 'SRC-REG-708',
        relation: '应急条例对应急组织职责与人员保障的规定，可用于说明人员信息为何要按职责登记。',
      },
      {
        id: 'SRC-REG-591',
        relation: '条例对营救与疏散的原则要求，可用于理解人员信息在事故中的位置。',
      },
    ],
  },
  'human-exposure|最后出现时间与暴露时间线': {
    why: '暴露时长影响医疗交接与人员状态核对，必须能回到具体的时间和地点。',
    what: ['最后出现或最后联系的时间', '各时段的暴露记录', '记录来源与记录人'],
    risk: '没有时间线，医疗交接只能凭印象，事故后的复查也无法进行。',
    boundary: '能说明记录中的时间与暴露过程；不构成医疗判断。',
    sources: [
      {
        id: 'SRC-AQ-3052',
        relation: '指挥程序对信息组织的要求，可用于说明时间线为何要跨部门对齐。',
      },
      {
        id: 'SRC-REG-708',
        relation: '应急条例对救援人员保障与医疗救护的总体要求，可用于理解交接记录的必要性。',
      },
    ],
  },
  'human-exposure|专业医疗记录': {
    why: '救治去向与专业判断只能由医疗机构给出，平台不应替它下结论。',
    what: ['接诊医疗机构', '救治去向与转送时间', '医疗机构的诊断与处置记录（由机构提供）'],
    risk: '用非专业人员转述代替医疗记录，会让交接链条断裂。',
    boundary: '能说明医疗记录的去向与内容来源；不能由平台推断伤情或预后。',
    sources: [
      {
        id: 'SRC-REG-708',
        relation: '应急条例对医疗救护与应急保障的规定，可用于说明医疗记录的来源。',
      },
      { id: 'SRC-REG-591', relation: '条例对事故救援中人员救护的原则要求，可用于理解交接环节。' },
    ],
  },
  'human-exposure|防护状态与防护装备记录': {
    why: '已防护与未防护的人员必须分开统计，否则会把两类风险混成一类。',
    what: ['人员防护装备配置记录', '防护状态及其持续时间', '更换与补给记录'],
    risk: '把防护状态当作已知，会让未防护人员被默认成已防护。',
    boundary: '能说明装备与防护状态的记录；不生成防护等级，也不评价防护是否足够。',
    sources: [
      {
        id: 'SRC-DRAFT-2026',
        relation: '征求意见稿拟对现场防护提出要求；仅为征求意见稿，不构成防护等级依据。',
      },
      {
        id: 'SRC-AQ-3052',
        relation: '指挥导则对救援力量组织的规定，可用于说明防护记录应纳入现场信息。',
      },
    ],
  },
  'human-exposure|洗消与医疗交接记录': {
    why: '从现场到医疗机构需要一条可以追查的交接链，洗消是这个链条上的关键一环。',
    what: ['洗消点位置与运行状态', '人员经过洗消的时间', '交接单与接收方'],
    risk: '交接记录缺失时，后续无法确认某个人是否经过洗消、由谁接收。',
    boundary: '能说明交接链条的记录；不生成洗消配方或流程，也不替代专业处置。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation:
          '条例第六十六至七十四条涉及危险化学品登记信息、事故应急预案、现场洗消与环境监测评估，是洗消记录的制度背景。',
      },
      {
        id: 'SRC-REG-708',
        relation: '应急条例对应急保障与救援人员保护的规定，可用于说明交接记录的作用。',
      },
    ],
  },

  /* ---------------------------------------------------- 火灾、爆炸与反应风险 */
  'fire-explosion|物质燃烧、氧化、反应与热稳定性资料': {
    why: '理解火灾与反应风险，需要物质自身的燃烧、氧化与热稳定性资料，而不是现场印象。',
    what: [
      'SDS 中的燃烧与反应性条目',
      '企业工艺资料中的温度与压力条件',
      '已知的禁配物与相容性信息',
    ],
    risk: '缺少物质资料时，容易把不相关的物质放在同一风险画像里，也会漏掉相互作用。',
    boundary: '能说明资料记载的特性；不能据此预测具体反应或选择处置方式。',
    sources: [
      {
        id: 'SRC-GBT-16483',
        relation: 'SDS 内容标准列出了反应性与稳定性的项目，可用于核对资料是否齐全。',
      },
      { id: 'SRC-REG-591', relation: '条例对危险化学品安全管理的要求，是这些资料应当存在的原因。' },
    ],
  },
  'fire-explosion|火情、烟气与热异常记录': {
    why: '火情与烟气形态会快速变化，需要连续记录而不是一个时间点的印象。',
    what: ['各时段视频或图像', '热成像记录（如设备具备）', '观察位置、角度与时间'],
    risk: '只有单一画面，容易把视角变化当成火势变化。',
    boundary: '能说明观察到的火情与热异常；不据此选择灭火介质或冷却方式。',
    sources: [
      { id: 'SRC-AQ-3052', relation: '指挥导则对现场情况掌握的要求，可用于组织连续观察记录。' },
    ],
  },
  'fire-explosion|相邻设备与库存信息': {
    why: '一次事故可能波及相邻装置与库存，需要提前知道旁边有什么。',
    what: ['相邻设备与储罐信息', '库存物质与数量', '设备之间的间距与管线连接（如有资料）'],
    risk: '不知道相邻危险源，会把单一泄漏的判断沿用在整个厂区。',
    boundary: '能说明资料记载的相邻危险源；不代表事故时刻的实际状态。',
    sources: [
      {
        id: 'SRC-GB-18218',
        relation: '重大危险源辨识标准要求掌握物质与数量，可用于说明相邻库存信息的作用。',
      },
      { id: 'SRC-REG-591', relation: '条例对储存与安全管理的要求，是库存资料应当可查的依据。' },
    ],
  },
  'fire-explosion|工艺资料': {
    why: '温度、压力等工艺条件决定了设备在事故中的实际状态，需要以企业资料为准。',
    what: ['工艺流程图与关键参数', '设备设计条件与安全设施', '最近的检修与变更记录'],
    risk: '缺少工艺资料，只能凭外观判断设备状态，容易误读压力异常等迹象。',
    boundary: '能说明设计条件与运行参数；不能替代专业工艺分析。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation: '条例对危险化学品生产与储存环节的要求，是工艺资料存在与调取的基础。',
      },
      {
        id: 'SRC-AQ-3052',
        relation: '指挥导则要求掌握事故单位情况，可用于说明工艺资料为何属于现场信息。',
      },
    ],
  },
  'fire-explosion|事故时间线': {
    why: '把各方报告按时间对齐，才能发现彼此不一致的地方。',
    what: ['报警、到场、处置等关键节点', '各节点的信息来源', '时间不一致之处'],
    risk: '没有统一时间线，各方叙述会各说各话，分歧也无法定位。',
    boundary: '能说明各节点的时间与来源；不能据此认定事故原因。',
    sources: [
      { id: 'SRC-AQ-3052', relation: '指挥程序对信息汇总的要求，可用于组织跨部门时间线。' },
      {
        id: 'SRC-COMMAND-QA',
        relation: '官方答复强调统一指挥与协同高效，可用于理解时间线统一口径的意义。',
      },
    ],
  },

  /* ---------------------------------------------------- 危险影响范围与周边态势不清 */
  'impact-area|气象条件记录': {
    why: '影响范围会随天气变化，需要带时间的气象记录，而不是一次性的天气描述。',
    what: ['风向风速的连续记录', '温度与稳定度', '记录来源与地点'],
    risk: '用单一时刻的气象描述，会忽略风向转变带来的方向性变化。',
    boundary: '能说明环境条件的变化；不能据此计算扩散范围或疏散距离。',
    sources: [
      { id: 'SRC-AQ-3052', relation: '指挥导则要求掌握现场环境情况，可用于说明气象记录的定位。' },
      {
        id: 'SRC-DRAFT-2026',
        relation: '征求意见稿拟对现场处置条件提出要求；仅为征求意见稿，仅供研究参考。',
      },
    ],
  },
  'impact-area|厂区总平面与设备布置': {
    why: '需要知道厂区内的道路、装置与相邻设施的关系，才能把监测点位和报告位置对应起来。',
    what: ['总平面图及其版本', '装置与储罐布置', '厂区出入口与道路'],
    risk: '没有总平面，位置描述只能靠地名，无法判断相对距离与相邻关系。',
    boundary: '能说明资料记载的布置；不能证明当前设施状态与图纸一致。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation: '条例对危险化学品单位安全管理的要求，是总平面与设备资料应当留存的原因。',
      },
    ],
  },
  'impact-area|道路、敏感点与周边人口设施': {
    why: '厂区外的道路、居民区与公共设施，是理解影响范围的另一半。',
    what: ['周边道路与通行情况', '居民区、学校、医院等敏感点', '资料的时间与来源'],
    risk: '只看厂区内，会漏掉紧邻的道路与人群；资料过期会漏掉新建或撤并。',
    boundary: '能说明资料记载的分布；不能判断这些位置此刻是否有人。',
    sources: [
      {
        id: 'SRC-REG-591',
        relation: '条例对危害区域测定与人员疏散的规定，可用于说明周边信息为何需要预先掌握。',
      },
      {
        id: 'SRC-COMMAND-QA',
        relation: '官方答复强调分级负责与属地协同，可用于理解周边信息的责任来源。',
      },
    ],
  },
  'impact-area|多位置监测记录': {
    why: '单一观测点不能代表完整的影响范围，需要在不同方向与距离上布点。',
    what: ['各点位的位置与时间', '设备类型与量程', '点位之间的可比性说明'],
    risk: '只有一个点位的读数，容易被读成整个区域的状况。',
    boundary: '能说明已布点位置的读数；点位不足时须标注待核实，不生成警戒半径。',
    sources: [
      { id: 'SRC-AQ-3052', relation: '指挥导则对现场信息掌握的要求，可用于组织多点位记录。' },
      {
        id: 'SRC-GB-18218',
        relation: '辨识标准说明物质与数量记录的作用，可用于理解为何要结合台账解释读数。',
      },
    ],
  },
  'impact-area|属地已发布信息': {
    why: '对外口径应当以属地已发布的信息为准，而不是平台自行归纳。',
    what: ['发布机构与发布时间', '发布内容原文', '后续更正或更新'],
    risk: '平台自行概括对外口径，可能与属地发布不一致，造成误解。',
    boundary: '能说明属地发布了什么；不代替属地发布，也不改写其内容。',
    sources: [
      {
        id: 'SRC-COMMAND-QA',
        relation: '官方答复强调统一指挥、分级负责，可用于说明对外口径的来源。',
      },
      { id: 'SRC-REG-591', relation: '条例对事故报告与信息发布的规定，是属地发布口径的制度依据。' },
    ],
  },

  /* ---------------------------------------------------- 环境污染与次生影响 */
  'environment-impact|空气、水体与土壤监测点位': {
    why: '事故控制不等于环境影响结束，需要分别记录空气、水体和土壤的观测位置。',
    what: ['各类介质的监测点位', '点位选择依据', '点位对应的责任部门'],
    risk: '只盯空气，会漏掉水体与土壤；点位不清，结果无法回到具体位置。',
    boundary: '能说明观测过哪些位置；不能说明未监测位置的情况。',
    sources: [
      { id: 'SRC-REG-591', relation: '条例涉及事故后的环境监测评估，是环境点位记录的制度背景。' },
    ],
  },
  'environment-impact|采样时间与样品编号': {
    why: '每条结果都要能回到具体样品，否则无法判断它代表哪一刻、哪一处。',
    what: ['采样时间与地点', '样品编号与保存方式', '采样人与所属机构'],
    risk: '没有编号与时间，检测结果只能作为笼统描述，无法复核。',
    boundary: '能说明样品的来源与时间；不能说明检测结果本身。',
    sources: [
      { id: 'SRC-REG-591', relation: '条例对环境监测评估的要求，可用于理解样品记录的必要性。' },
    ],
  },
  'environment-impact|检测机构与检测结果': {
    why: '需要区分有资质机构的检测结果与现场快速判断，两者可信度不同。',
    what: ['检测机构名称与资质', '检测项目与方法', '结果、单位与检出限'],
    risk: '把现场快速判断写成检测结果，会让环境结论失去依据。',
    boundary: '能说明机构出具的检测内容；环境影响的结论由有资质部门或机构作出。',
    sources: [
      { id: 'SRC-REG-591', relation: '条例对事故后环境监测评估的规定，可用于说明结论应由谁作出。' },
      {
        id: 'SRC-REG-708',
        relation: '应急条例对应急保障与部门职责的规定，可用于理解检测的责任分工。',
      },
    ],
  },
  'environment-impact|排水路径与径流资料': {
    why: '消防废水与洗消废水会沿排水系统流动，需要知道它们可能去哪里。',
    what: ['厂区排水管网资料', '雨水与污水去向', '已采取的排水控制状态记录'],
    risk: '忽略排水路径，会把厂区内的处置当成环境影响的终点。',
    boundary: '能说明资料记载的排水路径；不能据此判断污染是否已经发生。',
    sources: [
      {
        id: 'SRC-DRAFT-2026',
        relation: '征求意见稿拟对现场处置与次生影响提出要求；仅为征求意见稿，仅供研究参考。',
      },
      { id: 'SRC-REG-591', relation: '条例涉及封闭隔离与洗消，可用于理解废水去向为何需要记录。' },
    ],
  },
  'environment-impact|污染物资料与生态环境部门评估': {
    why: '污染物的性质由物质资料说明，环境影响由生态环境部门评估，平台只负责把两者放在一起。',
    what: ['物质与燃烧产物资料', '生态环境部门的评估意见', '评估时间与适用区域'],
    risk: '用平台推断代替主管部门评估，会让环境结论越权。',
    boundary: '能说明资料与评估意见；不替代生态环境部门作出结论。',
    sources: [
      { id: 'SRC-REG-708', relation: '应急条例对部门职责与应急保障的规定，可用于说明评估主体。' },
      { id: 'SRC-REG-591', relation: '条例对事故后环境监测评估的要求，是这一项的制度依据。' },
    ],
  },
};
